import { useWorkspaceText } from '../../interface-copy';
import { useCanvasItemName } from '../../artifacts/outlet';
import { focusFrameWhenShown } from '../../../../src/apps/runtime/frame-focus.mjs';
import { companyRequest, CompanyRequestError } from '@/company/api';
import { useNamespaceCopy } from '@/i18n/ns';
import buildWatchCopy from '@/i18n/ns/build-watch';
import { paymentRequiredMessage } from '../../../../src/usage/admission-error.mjs';
import { AppSaveControl } from './save-app';
import {
  createPreviewCollectionInventory,
  type PreviewCollectionInventory,
} from './collection-inventory';
import { useCompany } from '@/company/context';
import { useOptionalWorkspace } from '@/workspace/context';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { SessionChangedError, SessionUnverifiedError } from '@/lib/session-guard';
import { apiUrl, authHeaders } from '@/lib/shell';
import { downloadText } from '../../api';
import { DownloadOffer } from './download-offer';
import {
  handleLocalDownload,
  type LocalDownloadFile,
  type OfferedDownload,
} from './local-download';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  RuntimeFrame,
  type RuntimeIssued,
  type MountNamedRuntime,
} from '../../artifacts/site/runtime-frame';
import {
  AppBuilding,
  AppCanvasTools,
  SelectHint,
  appView,
  useAppBuild,
  useEditorShownSince,
  useAppSelect,
  useResolvedThread,
} from './live-build';
import { readPreviewView, type PreviewView } from './live-build-model';
import { APP_BUILDING_ID } from './tool-output';
import { NEW_APP_CANVAS } from '../new-app';
import { DeckExport } from './deck-export';
import { useEditingAppSelection } from '../editing-selection';
import { previewEditingSelection } from './editing-selection';
import type { EditingAppSelection } from '../../../../src/apps/tracer/editing-base.mjs';
import { PreviewBackendNotice } from './preview-backend-notice';

// Who is signed in and the App's team: read on the server through the saved App's authority.
const PREVIEW_IDENTITY_OPERATIONS = new Set(['identity.user', 'identity.can', 'team.list']);
// What the preview runs on the server. Anything else is refused as unsupported here, never
// answered with another operation's code.
const PREVIEW_SERVER_OPERATIONS = new Set([
  'collections.query',
  'collections.mutateBatch',
  'collections.lookupReceipt',
  'tools.call',
  'connections.list',
  'connections.read',
  'images.read',
  'functions.call',
  ...PREVIEW_IDENTITY_OPERATIONS,
]);

export type AppPreviewDescriptor = {
  id: string;
  title: string;
  threadId: string;
  src: string;
  issued: RuntimeIssued;
  /** A build's snapshot shown while the chat works: never saved, so it offers no Save. */
  live?: boolean;
  /** Actual prepared live workspace, including explicit fresh-null. */
  editingApp?: Readonly<EditingAppSelection> | null;
  /** A build that includes the kit's Deck: once saved, the canvas offers its PDF and PowerPoint. */
  deck?: boolean;
  savedApp?: {
    saved: {
      artifactId: string;
      versionId: string;
      revisionId: string;
      versionNumber: number;
      purpose: 'editor';
    };
    outputAvailable: boolean;
    threadId: string;
  };
};
export type DemoProduct = { name: string; price: number; stock: number };
export const demoProducts: readonly DemoProduct[] = [
  { name: 'Canvas tote', price: 18, stock: 24 },
  { name: 'Ceramic mug', price: 14, stock: 8 },
  { name: 'Linen napkin', price: 9, stock: 36 },
  { name: 'Desk lamp', price: 48, stock: 3 },
  { name: 'Notebook', price: 12, stock: 17 },
  { name: 'Glass vase', price: 26, stock: 0 },
  { name: 'Wool scarf', price: 32, stock: 5 },
  { name: 'Oak tray', price: 28, stock: 11 },
];
/**
 * A preview's asset ticket is finite (the server's view token lasts five minutes). The mounted frame renews it
 * once, shortly before it lapses, by re-reading the SAME authenticated descriptor; the renewed ticket is a fresh
 * frame (RuntimeFrame is keyed by ticket) and the old port is retired when the old frame unmounts. There is no
 * permanent ticket and no second renewal system.
 */
export const RENEW_LEAD_MS = 30_000;
export const RENEW_FLOOR_MS = 5_000;
export function renewalDelay(expiresAt: string, now = Date.now()): number | null {
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) ? Math.max(RENEW_FLOOR_MS, at - now - RENEW_LEAD_MS) : null;
}
/** The renewed descriptor must be the same preview in the same thread: anything else is not authorized. */
export const sameDescriptor = (value: AppPreviewDescriptor, id: string, threadId: string) =>
  value.id === id && value.threadId === threadId;

/** What the parent offers the preview's local Download action (the parent's own helper; no server call). */
export type PreviewLocalDownload = {
  activated(): boolean;
  download(name: string, text: string, mime: string): void;
  offer(file: OfferedDownload | null): void;
};
/**
 * Select and the view keeper of one frame (src/apps/runtime/preview-inspector.mjs, inside the frame): what the frame
 * reports arrives as untrusted page data, and the view to open at goes to the new frame on its own port.
 */
export type PreviewChannel = {
  view(): PreviewView | null;
  connected(send: (message: unknown) => void): void;
  message(data: { type: string }): void;
};
export const mountPreviewFor =
  (
    request: (
      operation: string,
      input: unknown,
      initialCollectionIds?: readonly string[],
      signal?: AbortSignal,
    ) => Promise<unknown>,
    local?: PreviewLocalDownload,
    inventory = createPreviewCollectionInventory(),
    channel?: PreviewChannel,
    /** A deck presents full screen from inside its frame (the browser shows its own exit hint). */
    fullscreen = false,
    onOperationRefused?: (operation: string, code: string) => void,
  ): MountNamedRuntime =>
  ({ window, iframe, issued, handlers, renew, onInvalidated }) => {
    // Bytes held for the host Download action belong to THIS ticket's frame only.
    const host = local && {
      activated: local.activated,
      download: local.download,
      offer: (file: LocalDownloadFile | null) =>
        local.offer(file === null ? null : { ...file, ticket: issued.ticket }),
    };
    const scope = issued.scope as { id: string; src: string; hostOrigin: string };
    let port: MessagePort | null = null;
    let releaseInventory = () => {};
    let active = true;
    let reads = new AbortController();
    const receive = (event: MessageEvent) => {
      if (
        !active ||
        event.source !== iframe.contentWindow ||
        event.origin !== 'null' ||
        event.data?.type !== 'poneva.preview.connect' ||
        event.data.nonce !== issued.ticket
      )
        return;
      const pair = new MessageChannel();
      reads.abort();
      reads = new AbortController();
      releaseInventory();
      port?.close();
      host?.offer(null);
      port = pair.port1;
      releaseInventory = inventory.bind(port);
      const ownedPort = port;
      port.onmessage = async (message) => {
        if (!active || ownedPort !== port) return;
        const input = message.data;
        if (input?.type === 'poneva.inspect' || input?.type === 'poneva.view') {
          channel?.message(input);
          return;
        }
        if (inventory.receive(input)) return;
        if (input.operation === 'runtime.ready') {
          await handlers['runtime.ready']?.request?.({});
          // A keyboard App hears keys only once its frame has focus; a rebuild's frame takes it once it is on show.
          if (active && ownedPort === port)
            focusFrameWhenShown(iframe, iframe.ownerDocument, {
              still: () => active && ownedPort === port,
            });
          return;
        }
        if (input.operation === 'local.download') {
          // Parent-only: bounded text from this frame's own port; never the server operation endpoint.
          const result = host
            ? handleLocalDownload(input.input, host)
            : { status: 'refused', code: 'download_unavailable' };
          port?.postMessage({ id: input.id, result });
          return;
        }
        if (input.operation === 'collections.query' && input.collectionId === 'demo_products') {
          port?.postMessage({ id: input.id, rows: demoProducts });
        } else if (PREVIEW_SERVER_OPERATIONS.has(input.operation)) {
          try {
            const ids = input.operation.startsWith('collections.')
              ? inventory.admit(input.initialCollectionIds)
              : undefined;
            const result = await request(input.operation, input.input, ids, reads.signal);
            if (active && ownedPort === port) port?.postMessage({ id: input.id, result });
          } catch (error) {
            if (active && ownedPort === port)
              port?.postMessage({
                id: input.id,
                // A server failure stays "unavailable", never an access decision, unless the
                // server classified it as a data read outcome (a slow query, an unreachable
                // database) with its numbers: the App receives that so it can say what happened.
                error:
                  error instanceof CompanyRequestError
                    ? error.status < 500 || error.detail !== null
                      ? error.code
                      : PREVIEW_IDENTITY_OPERATIONS.has(input.operation)
                        ? 'UNAVAILABLE'
                        : 'preview_data_unavailable'
                    : error instanceof SessionChangedError ||
                        error instanceof SessionUnverifiedError
                      ? error.message
                      : 'data_unavailable',
                ...(error instanceof CompanyRequestError && error.detail
                  ? { detail: error.detail }
                  : {}),
                // The company's plan, credits or the person's limit refused it: the App says the sentence for that
                // reason (never the answer's own text).
                ...(error instanceof CompanyRequestError &&
                error.status === 402 &&
                error.code === 'payment_required'
                  ? { message: paymentRequiredMessage(error.reason) }
                  : {}),
              });
            if (
              active &&
              ownedPort === port &&
              input.operation === 'functions.call' &&
              error instanceof CompanyRequestError &&
              error.code === 'preview_backend_requires_saved_app'
            )
              onOperationRefused?.(input.operation, error.code);
          }
        } else port?.postMessage({ id: input.id, error: 'preview_operation_unsupported' });
      };
      iframe.contentWindow!.postMessage(
        { type: 'poneva.preview.connect', nonce: issued.ticket },
        '*',
        [pair.port2],
      );
      const restore = channel?.view();
      if (restore) port.postMessage({ type: 'poneva.view', restore });
      channel?.connected((data) => {
        if (active && ownedPort === port) ownedPort.postMessage(data);
      });
    };
    window.addEventListener('message', receive);
    iframe.setAttribute('sandbox', 'allow-scripts allow-forms');
    if (fullscreen) iframe.setAttribute('allow', 'fullscreen *');
    iframe.setAttribute('referrerpolicy', 'no-referrer');
    // A rebuilt App opens on the route the person was on.
    iframe.src = scope.src + (channel?.view()?.hash ?? '');
    // Renew before this ticket lapses. The server renews an open frame IN PLACE (same ticket, fresh expiry): the
    // document keeps running with its unsaved state, and the next renewal is armed from the new expiry. A different
    // ticket (an older server, or a lapsed one) remounts the frame through its key, which clears this timer. A failed
    // renewal keeps the current frame and does not retry.
    let renewal: ReturnType<typeof setTimeout> | null = null;
    const arm = (expiresAt: string) => {
      const delay = renewalDelay(expiresAt);
      if (delay === null) return;
      renewal = setTimeout(() => {
        renewal = null;
        if (!active) return;
        void Promise.resolve(renew(scope))
          .then((next) => {
            const renewed = next as { ticket?: unknown; expiresAt?: unknown } | null;
            if (
              active &&
              renewed?.ticket === issued.ticket &&
              typeof renewed.expiresAt === 'string'
            )
              arm(renewed.expiresAt);
          })
          .catch(() => {});
      }, delay);
    };
    arm(issued.expiresAt);
    return {
      scope,
      transport: port,
      invalidate() {
        active = false;
        reads.abort();
        if (renewal !== null) clearTimeout(renewal);
        renewal = null;
        host?.offer(null);
        releaseInventory();
        port?.close();
        window.removeEventListener('message', receive);
        onInvalidated();
      },
    };
  };
export function requestPreviewFor(previewId: string, threadId: string, sessionFetch: typeof fetch) {
  return async (
    operation: string,
    input: unknown,
    initialCollectionIds?: readonly string[],
    signal?: AbortSignal,
  ) => {
    if (operation !== 'images.read' || Object.hasOwn((input as object) ?? {}, 'imageId'))
      return companyRequest(
        `/api/v1/company/app-previews/${encodeURIComponent(previewId)}/operation?threadId=${encodeURIComponent(threadId)}`,
        'POST',
        {
          operation,
          input,
          ...(initialCollectionIds === undefined ? {} : { initialCollectionIds }),
        },
        operation === 'connections.read'
          ? (url, init) => sessionFetch(url, { ...init, signal })
          : sessionFetch,
      );
    return readPreviewImage(input, threadId, sessionFetch);
  };
}

type Frame = { value: AppPreviewDescriptor; inventory: PreviewCollectionInventory };
const LIVE_AREA = { position: 'relative', height: '100%', minHeight: 320 } as const;
// Top and bottom fill the canvas; the sides take the full-screen gutter (live-build.css sets --ap-live-gutter), so an inline
// `inset: 0` can never override it. Without that stylesheet the App still fills the canvas edge to edge.
const SHOWN_FRAME = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  insetInline: 'var(--ap-live-gutter, 0px)',
} as const;
const INCOMING_FRAME = { ...SHOWN_FRAME, opacity: 0, pointerEvents: 'none' } as const;

/**
 * A chat's App on its canvas. It follows the chat's newest build of the App (a live build while the chat works, then
 * its finished preview), falling back to the preview it was opened with. Each new build loads unseen behind the one on
 * show and replaces it once it has drawn, at the same scroll and route, so the person watches the App take shape.
 * `waiting` is what a new App's panel shows before the chat starts building (apps/new-app.ts).
 */
export function AppPreview({
  id,
  threadId: linkedThread,
  waiting = null,
  pinned = false,
  editor = false,
}: {
  id: string;
  threadId: string;
  waiting?: ReactNode;
  /** Show exactly this build (chosen from the chat's history), not the chat's newest. */
  pinned?: boolean;
  /** An App's saved editor that Change with Chat opened: only builds made after it replace it. */
  editor?: boolean;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const sessionFetch = useSessionBoundFetch();
  const company = useCompany();
  const workspace = useOptionalWorkspace();
  // A linked chat that is still opening has a placeholder thread: nothing is read for it.
  const threadId = useResolvedThread(linkedThread) ?? '';
  const build = useAppBuild(threadId);
  const scope = JSON.stringify([
    threadId,
    company.data?.company?.id,
    company.data?.membership?.userId,
  ]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const opened = id === APP_BUILDING_ID || id === NEW_APP_CANVAS.id ? null : id;
  // Change with Chat's saved editor is newer than every build the chat made before it opened: it shows until an answer
  // that started after it builds (its live build, then its finished preview, which also takes over the canvas).
  const editorSince = useEditorShownSince(
    editor && opened && threadId ? `${threadId}:${opened}` : null,
  );
  const newer =
    editorSince === null ? [] : (build?.recent ?? []).filter((made) => made.answer > editorSince);
  const wanted = JSON.stringify(
    threadId
      ? [
          ...new Set(
            (pinned && opened
              ? [opened]
              : editorSince !== null
                ? [newer.at(-1)?.id, opened, newer.filter((made) => made.finished).at(-1)?.id]
                : [build?.latest, opened, build?.final]
            ).filter((value): value is string => !!value),
          ),
        ]
      : [],
  );
  const [frames, setFrames] = useState<{
    scope: string;
    shown: Frame | null;
    incoming: Frame | null;
    failed: boolean;
    /** Every build was refused by the server (403/404): the preview is gone, not just unreachable for now. */
    refused?: boolean;
    /** The builds asked for when that was found (`wanted`): another preview opened since was not refused. */
    refusedFor?: string;
  }>({ scope, shown: null, incoming: null, failed: false });
  const current =
    frames.scope === scope
      ? frames
      : { shown: null, incoming: null, failed: false, refused: false, refusedFor: '' };
  const { shown, incoming } = current;
  // A committed incoming frame already names the new saved version, even
  // while its document is drawing behind the previous live frame.
  const editingPreview = incoming?.value ?? shown?.value;
  useEditingAppSelection(
    `app-preview:${id}`,
    editingPreview
      ? previewEditingSelection(editingPreview, threadId)
      : id === NEW_APP_CANVAS.id
        ? null
        : undefined,
    // Nothing on show because the server refused this preview (e.g. a build from before a redeploy). Change with
    // Chat's saved editor stays an App the chat cannot name: its way back is Apps.
    !editor &&
      current.failed &&
      current.refused === true &&
      current.refusedFor === wanted &&
      !shown &&
      !incoming,
  );
  const framesNow = useRef(current);
  framesNow.current = current;
  // The one ready local download of the shown frame (host Download action); gone with its frame.
  const [offer, setOffer] = useState<OfferedDownload | null>(null);
  const [backendNotice, setBackendNotice] = useState<{ scope: string; ticket: string } | null>(
    null,
  );
  const local = useMemo<PreviewLocalDownload>(
    () => ({
      activated: () => navigator.userActivation?.isActive === true,
      download: downloadText,
      offer: setOffer,
    }),
    [],
  );
  const descriptor = useCallback(
    (previewId: string, signal: AbortSignal, renew?: string) =>
      companyRequest<AppPreviewDescriptor>(
        `/api/v1/company/app-previews/${encodeURIComponent(previewId)}?threadId=${encodeURIComponent(threadId)}${renew ? `&renew=${encodeURIComponent(renew)}` : ''}`,
        'GET',
        undefined,
        (input, init) => sessionFetch(input, { ...init, signal }),
      ).then((value) => {
        if (!sameDescriptor(value, previewId, threadId)) throw new Error('preview_not_authorized');
        return value;
      }),
    [threadId, sessionFetch],
  );
  const load = (value: AppPreviewDescriptor) =>
    setFrames((was) => ({
      scope,
      shown: was.scope === scope ? was.shown : null,
      incoming: { value, inventory: createPreviewCollectionInventory() },
      failed: false,
    }));
  const renewed = (value: AppPreviewDescriptor) =>
    setFrames((was) => {
      const same = (frame: Frame | null) =>
        frame?.value.issued.ticket === value.issued.ticket ? { ...frame, value } : frame;
      return { ...was, shown: same(was.shown), incoming: same(was.incoming) };
    });
  useEffect(() => {
    const abort = new AbortController();
    const candidates = JSON.parse(wanted) as string[];
    const { shown: on, incoming: next } = framesNow.current;
    if (!candidates.length || [on, next].some((frame) => frame?.value.id === candidates[0])) return;
    void (async () => {
      // The newest build first; a build that cannot be opened (a later one replaced it) falls back.
      let refused = true;
      for (const candidate of candidates) {
        if (framesNow.current.shown?.value.id === candidate) return;
        try {
          const value = await descriptor(candidate, abort.signal);
          if (!abort.signal.aborted && currentScope.current === scope) load(value);
          return;
        } catch (error) {
          if (abort.signal.aborted) return;
          // Only the server saying no (403/404) means the preview is gone; a passing failure (a 5xx, the network, a
          // changed session) does not.
          refused &&=
            error instanceof CompanyRequestError && (error.status === 403 || error.status === 404);
        }
      }
      if (currentScope.current === scope && !framesNow.current.shown)
        setFrames({
          scope,
          shown: null,
          incoming: null,
          failed: true,
          refused,
          refusedFor: wanted,
        });
    })();
    return () => abort.abort();
  }, [scope, wanted, descriptor]);
  useEffect(() => {
    setBackendNotice(null);
    return () => setOffer(null);
  }, [scope]);

  // Select and the view keeper speak with the shown frame only.
  const senders = useRef(new Map<string, (message: unknown) => void>());
  const shownTicket = shown?.value.issued.ticket ?? null;
  const shownTicketNow = useRef(shownTicket);
  shownTicketNow.current = shownTicket;
  const visibleTicketNow = useRef(shownTicket ?? incoming?.value.issued.ticket ?? null);
  visibleTicketNow.current = shownTicket ?? incoming?.value.issued.ticket ?? null;
  const send = useCallback(
    (message: unknown) => {
      if (shownTicket) senders.current.get(shownTicket)?.(message);
    },
    [shownTicket],
  );
  const select = useAppSelect(send);
  const buildCopy = useNamespaceCopy(buildWatchCopy);
  // The chat's list names this item by the build's own title.
  // A pinned canvas is named by the build it was pinned to, never by a different build still on show while it loads.
  const named =
    pinned && opened
      ? [shown, incoming].find((frame) => frame?.value.id === opened)
      : (shown ?? incoming);
  useCanvasItemName(named?.value.title);
  // Select belongs to this conversation's App: another chat's App starts with it off.
  const { setSelecting } = select;
  useEffect(() => setSelecting(false), [scope, setSelecting]);
  const selectNow = useRef(select);
  selectNow.current = select;
  const channel = (ticket: string): PreviewChannel => ({
    view: () => appView.read(threadId),
    connected: (post) => senders.current.set(ticket, post),
    message: (data) => {
      if (ticket !== shownTicketNow.current) return;
      if (data.type !== 'poneva.view')
        return selectNow.current.receive(data as { on?: unknown; selected?: unknown });
      const view = readPreviewView((data as { scroll?: unknown }).scroll);
      if (view) appView.keep(threadId, view);
    },
  });
  const promote = (ticket: string) => {
    setFrames((was) =>
      was.incoming?.value.issued.ticket === ticket
        ? { ...was, shown: was.incoming, incoming: null }
        : was,
    );
    for (const key of senders.current.keys())
      if (key !== ticket && key !== framesNow.current.incoming?.value.issued.ticket)
        senders.current.delete(key);
  };
  const showBackendNotice = (ticket: string) => {
    if (currentScope.current !== scope || visibleTicketNow.current !== ticket) return;
    setBackendNotice({ scope, ticket });
  };

  if (current.failed && !shown)
    return <p role="alert">{uiText('This App could not be opened.')}</p>;
  if (!shown && !incoming && !build?.building && !build?.latest && waiting) return <>{waiting}</>;
  if (!threadId && opened) return <p role="status">{uiText('Opening App…')}</p>;
  const frameList = [shown, incoming].filter((frame): frame is Frame => frame !== null);
  const visible = shown ?? incoming;
  return (
    <>
      {shown && !shown.value.live && (
        <AppSaveControl
          threadId={threadId}
          title={shown.value.title}
          itemKey={`app-preview:${shown.value.id}`}
          saved={
            shown.value.savedApp?.threadId === threadId && shown.value.savedApp.outputAvailable
              ? shown.value.savedApp.saved
              : null
          }
          readInventory={async () => {
            const ids = await shown.inventory.read();
            if (currentScope.current !== scope) throw Error('collection_inventory_unavailable');
            return ids;
          }}
        />
      )}
      {shown?.value.deck &&
        !shown.value.live &&
        shown.value.savedApp?.threadId === threadId &&
        shown.value.savedApp.outputAvailable && (
          <DeckExport title={shown.value.title} version={shown.value.savedApp.saved} />
        )}
      <AppCanvasTools
        selecting={select.selecting}
        onSelecting={select.setSelecting}
        canSelect={Boolean(shown)}
        updating={Boolean(shown && (build?.building || incoming))}
      />
      {/* The chat's newest preview drew nothing: said above the App on show, which stays (and so does the App the next
          turn edits) until the chat builds again. */}
      {build?.notMounted && (
        <div className="ap-not-mounted" role="status" data-testid="app-not-mounted">
          <span className="ap-not-mounted-dot" aria-hidden="true" />
          <span>{buildCopy.notMounted}</span>
        </div>
      )}
      {backendNotice?.scope === scope && backendNotice.ticket === visible?.value.issued.ticket && (
        <PreviewBackendNotice
          key={backendNotice.ticket}
          saved={
            visible?.value.savedApp?.threadId === threadId && visible.value.savedApp.outputAvailable
              ? visible.value.savedApp.saved
              : null
          }
          send={sessionFetch}
          openCanvas={(target) => workspace?.openCanvas(target)}
          openApps={() => workspace?.navigate('apps')}
          scopeKey={`${scope}:${backendNotice.ticket}`}
        />
      )}
      {offer && offer.ticket === shown?.value.issued.ticket && (
        <DownloadOffer offer={offer} onDownloaded={() => setOffer(null)} />
      )}
      {/* The frame's layout lives here, not only in live-build.css: the App fills the canvas whatever stylesheet the
          page has, and the next build stays unseen until it replaces the one on show. */}
      <div className="ap-live" style={LIVE_AREA}>
        {frameList.map((frame) => (
          <div
            key={frame.value.issued.ticket}
            className="ap-live-frame"
            style={frame === incoming && shown ? INCOMING_FRAME : SHOWN_FRAME}
            data-incoming={frame === incoming && shown ? '' : undefined}
            inert={frame === incoming && shown ? true : undefined}
          >
            <RuntimeFrame
              title={frame.value.title}
              issued={frame.value.issued}
              mount={mountPreviewFor(
                requestPreviewFor(frame.value.id, threadId, sessionFetch),
                local,
                frame.inventory,
                channel(frame.value.issued.ticket),
                frame.value.deck === true,
                (operation, code) => {
                  if (
                    operation === 'functions.call' &&
                    code === 'preview_backend_requires_saved_app'
                  )
                    showBackendNotice(frame.value.issued.ticket);
                },
              )}
              handlers={{
                'runtime.ready': {
                  request: async () => {
                    promote(frame.value.issued.ticket);
                    return null;
                  },
                },
              }}
              // A ticket is finite. The server renews an open frame in place (same ticket, fresh expiry); any other
              // ticket swaps in like a new build.
              renew={async () => {
                const ticket = frame.value.issued.ticket;
                const value = await descriptor(
                  frame.value.id,
                  new AbortController().signal,
                  ticket,
                );
                if (currentScope.current !== scope) return null;
                if (value.issued.ticket === ticket) renewed(value);
                else if (shownTicketNow.current === ticket) load(value);
                return value.issued;
              }}
              retire={async () => null}
            />
          </div>
        ))}
        {!shown && (build?.building || !opened) && <AppBuilding build={build} />}
        {select.selecting && shown && <SelectHint />}
      </div>
    </>
  );
}

/** An App's image read: the PNG of a chat tool result, by its own selection, as a data URL for the frame. */
async function readPreviewImage(input: unknown, threadId: string, sessionFetch: typeof fetch) {
  const image = input as { toolCallId?: string; imagePath?: unknown };
  if (typeof image?.toolCallId !== 'string' || !Array.isArray(image.imagePath))
    throw new Error('image_selection_invalid');
  const response = await sessionFetch(
    apiUrl(
      `/api/v1/company/threads/${encodeURIComponent(threadId)}/tool-calls/${encodeURIComponent(image.toolCallId)}/image?path=${encodeURIComponent(JSON.stringify(image.imagePath))}`,
    ),
    { credentials: 'same-origin', headers: authHeaders() },
  );
  if (!response.ok)
    throw new Error((await response.json().catch(() => null))?.error ?? 'image_unavailable');
  if (response.headers.get('content-type') !== 'image/png') throw new Error('image_unavailable');
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.length > 16777216) throw new Error('image_too_large');
  let encoded = '';
  for (let at = 0; at < bytes.length; at += 16384)
    encoded += String.fromCharCode(...bytes.subarray(at, at + 16384));
  return {
    src: `data:image/png;base64,${btoa(encoded)}`,
    width: Number(response.headers.get('x-image-width')),
    height: Number(response.headers.get('x-image-height')),
  };
}
